import React from 'react';
import { ChevronDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { formatTime } from '@odo/shared-ui';

const SLOT_INTERVAL_MINUTES = 15;
const CLINIC_DAY_START_MINUTES = 7 * 60; // 7:00 AM
const CLINIC_DAY_END_MINUTES = 19 * 60; // 7:00 PM
const NO_TIME_VALUE = '';

function buildTimeSlots(): string[] {
    const slots: string[] = [];
    for (
        let m = CLINIC_DAY_START_MINUTES;
        m <= CLINIC_DAY_END_MINUTES;
        m += SLOT_INTERVAL_MINUTES
    ) {
        const hours = String(Math.floor(m / 60)).padStart(2, '0');
        const minutes = String(m % 60).padStart(2, '0');
        slots.push(`${hours}:${minutes}`);
    }
    return slots;
}

const TIME_SLOTS = buildTimeSlots();

interface TimeFieldProps {
    /** 'HH:MM' 24h, or '' for no time set: the same value shape the
     *  backend contract and draft state already use. */
    value: string;
    onChange: (time: string) => void;
}

/** Styled `<select>` of 15-minute slots (native, for free keyboard/screen-
 *  reader support) plus "No time"; displays 12h, value stays 24h 'HH:MM'. */
const TimeField: React.FC<TimeFieldProps> = ({ value, onChange }) => {
    // Off-grid legacy times get added as a real option instead of falling back to "No time".
    const options =
        value && !TIME_SLOTS.includes(value) ? [...TIME_SLOTS, value].sort() : TIME_SLOTS;

    return (
        <div className="relative">
            <ClockIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
                value={value || NO_TIME_VALUE}
                onChange={(e) => onChange(e.target.value)}
                className="w-full appearance-none rounded-lg border border-gray-200 bg-white pl-9 pr-8 py-2.5 text-sm text-gray-800 hover:border-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
            >
                <option value={NO_TIME_VALUE}>No time</option>
                {options.map((slot) => (
                    <option key={slot} value={slot}>
                        {formatTime(slot)}
                    </option>
                ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
    );
};

export default TimeField;
